import React, { useState } from "react";
import './BalanceSheet.css';
import { Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'; // Import charting components

interface Props {
  incomeStatement: IncomeStatementProps;
}

interface LineItem {
  name: string;
  value: number;
  account_id?: string;
  line_items?: LineItem[];
}

export interface IncomeStatementProps {
  id: number;
  period_start: string;
  period_end: string;
  revenue: LineItem[];
  cost_of_goods_sold: LineItem[];
  operating_expenses: LineItem[];
  gross_profit: number;
  operating_profit: number;
  non_operating_revenue: LineItem[];
  non_operating_expenses: LineItem[];
  earnings_before_taxes: number | null;
  taxes: LineItem[];
  net_profit: number;
}

const IncomeStatement: React.FC<Props> = ({ incomeStatement }) => {
  console.log(incomeStatement);
  const [activeSection, setActiveSection] = useState<string>('Revenue');
  const [collapsedItems, setCollapsedItems] = useState<{ [key: string]: boolean }>({});

  // Handle toggling of line item collapse/expand
  const toggleCollapse = (itemName: string) => {
    setCollapsedItems(prevState => ({
      ...prevState,
      [itemName]: !prevState[itemName],
    }));
  };

  // Render line items recursively with collapsible functionality
  const renderLineItems = (lineItems?: LineItem[], level: number = 0) => {
    if (!lineItems || lineItems.length === 0) return null;

    return (
      <ul className="line-items">
        {lineItems.map((item, index) => (
          <li key={`${item.name}-${index}`} className="line-item" style={{ marginLeft: `${level * 20}px` }}>
            <div className="line-item-header" onClick={() => toggleCollapse(item.name)}>
              <div className="line-item-name">
                <strong>{item.name}</strong>
              </div>

              <div className="line-item-value">
                ${item.value.toLocaleString()}
              </div>

              {item.line_items && (
                <span className="collapse-toggle">
                  {collapsedItems[item.name] ? '+' : '-'}
                </span>
              )}
            </div>
            {item.account_id && <span className="account-id">(Account ID: {item.account_id})</span>}
            {item.line_items && !collapsedItems[item.name] && renderLineItems(item.line_items, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  // Render sections for different parts of the income statement
  const renderSection = (title: string, data: LineItem[]) => (
    <section className={`balance-sheet-section ${activeSection === title ? 'active' : ''}`}>
      <h3 className="section-title">{title}</h3>
      <div>{renderLineItems(data)}</div>
    </section>
  );

  // Handle switching between sections (Revenue, Cost of Goods Sold, etc.)
  const handleTabChange = (section: string) => {
    setActiveSection(section);
  };

  // Prepare pie chart data
  const pieData = [
    { name: 'Gross Profit', value: incomeStatement.gross_profit },
    { name: 'Revenue', value: incomeStatement.revenue.reduce((sum, item) => sum + item.value, 0) },
    { name: 'Cost Of Goods Sold', value: incomeStatement.cost_of_goods_sold.reduce((sum, item) => sum + item.value, 0) },
    { name: 'Non-Operating Expenses', value: incomeStatement.non_operating_expenses.reduce((sum, item) => sum + item.value, 0) },
    { name: 'Non-Operating Revenue', value: incomeStatement.non_operating_revenue.reduce((sum, item) => sum + item.value, 0) },
    { name: 'Operating Profit', value: incomeStatement.operating_profit },
    { name: 'Operating Expenses', value: incomeStatement.operating_expenses.reduce((sum, item) => sum + item.value, 0) },
  ];

  return (
    <div className="balance-sheet-container">
      <div className="balance-sheet-header">
        <h1 className="header-title">Income Statement</h1>
        <h2 className="header-subtitle">
          Period: {incomeStatement.period_start} to {incomeStatement.period_end}
        </h2>
      </div>

      {/* Quick Overview Section */}
      <div className="summary-section">
        <h3 className="summary-title">Quick Overview</h3>
        <div className="summary-cards">
          <div className="summary-card">
            <h4>Gross Profit</h4>
            <p>${incomeStatement.gross_profit.toLocaleString()}</p>
          </div>
          <div className="summary-card">
            <h4>Operating Profit</h4>
            <p>${incomeStatement.operating_profit.toLocaleString()}</p>
          </div>
          <div className="summary-card">
            <h4>Net Profit</h4>
            <p>${incomeStatement.net_profit.toLocaleString()}</p>
          </div>
        </div>

        {/* Pie Chart */}
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              outerRadius={80}
              fill="#8884d8"
              label={({ value }) => {
                // Format the number to match the format in summary
                return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              }}
            >
              <Cell key="revenue" fill="#82ca9d" />
              <Cell key="costOfGoodsSold" fill="#ff7300" />
              <Cell key="operatingExpenses" fill="#8884d8" />
            </Pie>
            <Tooltip
              formatter={(value: number) => value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Tabs for switching between sections */}
      <div className="tabs">
        <button
          className={`tab ${activeSection === 'Revenue' ? 'active' : ''}`}
          onClick={() => handleTabChange('Revenue')}
        >
          Revenue
        </button>
        <button
          className={`tab ${activeSection === 'Cost of Goods Sold' ? 'active' : ''}`}
          onClick={() => handleTabChange('Cost of Goods Sold')}
        >
          Cost of Goods Sold
        </button>
        <button
          className={`tab ${activeSection === 'Operating Expenses' ? 'active' : ''}`}
          onClick={() => handleTabChange('Operating Expenses')}
        >
          Operating Expenses
        </button>
        <button
          className={`tab ${activeSection === 'Non-Operating Revenue' ? 'active' : ''}`}
          onClick={() => handleTabChange('Non-Operating Revenue')}
        >
          Non-Operating Revenue
        </button>
        <button
          className={`tab ${activeSection === 'Non-Operating Expenses' ? 'active' : ''}`}
          onClick={() => handleTabChange('Non-Operating Expenses')}
        >
          Non-Operating Expenses
        </button>
        <button
          className={`tab ${activeSection === 'Taxes' ? 'active' : ''}`}
          onClick={() => handleTabChange('Taxes')}
        >
          Taxes
        </button>
      </div>

      {/* Render the active section */}
      <div className="balance-sheet-content">
        {activeSection === 'Revenue' && renderSection("Revenue", incomeStatement.revenue)}
        {activeSection === 'Cost of Goods Sold' && renderSection("Cost of Goods Sold", incomeStatement.cost_of_goods_sold)}
        {activeSection === 'Operating Expenses' && renderSection("Operating Expenses", incomeStatement.operating_expenses)}
        {activeSection === 'Non-Operating Revenue' && renderSection("Non-Operating Revenue", incomeStatement.non_operating_revenue)}
        {activeSection === 'Non-Operating Expenses' && renderSection("Non-Operating Expenses", incomeStatement.non_operating_expenses)}
        {activeSection === 'Taxes' && renderSection("Taxes", incomeStatement.taxes)}
      </div>
    </div>
  );
};

export default IncomeStatement;
